import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import { APP_SLUGS, type AppSlug } from "@app/portal-sdk";
import { Alert, AppIconTile, AppTabs, Box, Button, EmptyState, SectionCard, Stack, Typography } from "@app/ui-kit";
import { APP_REGISTRY } from "../../apps/registry";
import { MAINTENANCE_TABS } from "./tabs";

/**
 * MNT-3: /admin/apps/:slug, the application's maintenance screen. Its mark and name, the registry's sentence saying
 * what BKDP maintains and who reads it, the tier stated because editing here is consequential, and AppTabs with one
 * tab per maintained thing, the same component the client-facing applications use. No client is reachable from
 * here: no picker, no column, no route that takes a client id; that is the Clients section's job (decision 1).
 */
export const AppMaintenance = () => {
  const { slug = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const known = (APP_SLUGS as readonly string[]).includes(slug);
  const def = known ? APP_REGISTRY[slug as AppSlug] : undefined;
  const back = <Button component={RouterLink} to="/admin/apps" variant="outlined">Back to applications</Button>;
  if (!def) return <EmptyState eyebrow="Applications" title="No such application" description={`Nothing is called "${slug}" here.`} action={back} />;
  if (!def.maintained) return <EmptyState eyebrow="Applications" title={`Nothing to maintain for ${def.name}`} description="It declares nothing BKDP maintains for it, so it has no screen here." action={back} />;
  const m = def.maintained;
  const tabParam = params.get("tab");
  const tab = m.things.some((t) => t.key === tabParam) ? tabParam! : m.things[0]?.key ?? "";
  const thing = m.things.find((t) => t.key === tab);
  const Screen = thing ? MAINTENANCE_TABS[def.slug]?.[thing.key] : undefined;
  return (
    <Stack spacing={3}>
      <Box sx={{ pb: 2, borderBottom: "1.5px solid", borderColor: "divider" }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <AppIconTile icon={def.icon} color={def.iconColor} />
          <Box>
            <Typography variant="overline" sx={{ color: "secondary.main", display: "block" }}>Admin Console · Applications</Typography>
            <Typography variant="h3" sx={{ color: "text.primary" }} data-testid="app-name">{def.name}</Typography>
          </Box>
        </Stack>
        <Typography variant="body1" sx={{ mt: 1.5 }} data-testid="maintained-sentence">{m.sentence}</Typography>
        {/* MNT-3 criterion 3: the tier, said, because a change here is not a change to one client's list. */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} data-testid="tier-line">These rows belong to no client. A change here reaches every client that seeds from them afterwards.</Typography>
      </Box>
      <AppTabs groups={[{ name: "Maintained", items: m.things.map((t) => ({ key: t.key, label: t.label })) }]} value={tab} onChange={(key) => setParams({ tab: key }, { replace: true })} />
      {thing && <Typography variant="body2" color="text.secondary" data-testid="thing-description">{thing.description}</Typography>}
      {thing?.maintainedBy === "elsewhere" && (
        <SectionCard eyebrow="Maintained elsewhere" title={`Where ${thing.label.toLowerCase()} is maintained`}>
          <Typography variant="body2" data-testid="elsewhere">{thing.elsewhere}</Typography>
        </SectionCard>
      )}
      {/* MNT-5 criterion 3: a thing maintained elsewhere may be shown here, never edited here. */}
      {thing?.maintainedBy === "elsewhere" && Screen && <Screen />}
      {thing?.maintainedBy === "screen" && (Screen ? <Screen /> : <Alert severity="error">{thing.label} declares a screen and none is registered for it. That is a defect in the registry, not something to work around here.</Alert>)}
    </Stack>
  );
};
